import React, { useEffect, useMemo, useState } from 'react';
import { Block } from '@klp/design';
import type { Theme } from '@material-ui/core';

import type { IKommentar } from '../services/rtkApi';
import { useLazyGetKommentarerQuery } from '../services/rtkApi';
import { useDispatch, useSelector } from 'react-redux';
import {
    kommentarerSelectorActions,
    kommentarerSelectors,
} from '../store/kommentarerSlice';
import { LocalLoader } from '@klp/design/Loader';
import { KommentarDetaljer } from './KommentarDetaljer';
import { contextSelectors } from '@klp/contextSelector';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import Carousel from 'nuka-carousel';
import classNames from 'classnames';
import { FremhevdeKommentarerCard } from './FremhevdeKommentarerCard';

const SAMMENDRAG_MAKS_ANTALL_TEGN = 100;

const styles = (theme: Theme) => ({
    button: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: theme.spacing(4),
        height: theme.spacing(4),
        border: '2px solid transparent',
        borderColor: theme.palette.primary?.main,
        borderRadius: '50%',
        fontSize: '1.2em',
        fontWeight: 'bold',
        transition: theme.layout.transition,
        cursor: 'pointer',
        boxShadow: theme.shadows[1],
        marginTop: `calc(${theme.spacing(4)}px / -2 - ${theme.spacing(2)}px)`,
        '&:hover': {
            backgroundColor: `${theme.palette.tertiary?.dark} !important`,
            boxShadow: theme.shadows[2],
        },
        '&.prev': {
            marginLeft: `calc(${theme.spacing(2)} * -1px)`,
            '&[disabled]': {
                opacity: '0 !important',
                display: 'none',
            },
        },
        '&.next': {
            marginRight: `calc(${theme.spacing(2)} * -1px)`,
            '&[disabled]': {
                opacity: '0 !important',
                display: 'none',
            },
        },
    },
    carousel: {
        paddingBottom: theme.spacing(4),
        marginBottom: theme.spacing(1),
        marginLeft: `calc(${theme.spacing(1)} * -1px)`,
        marginRight: `calc(${theme.spacing(1)} * -1px)`,
        width: `calc(100% + ${theme.spacing(1)} * 2px) !important`,
        '& .slide': {
            paddingLeft: 0,
            paddingRight: 0,
            '& > .CardWrapper': {
                height: '100%',
                '& > .Card': {
                    paddingTop: 0,
                    paddingBottom: 0,
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1),
                    minHeight: theme.spacing(26),
                    overflow: 'hidden',
                    position: 'relative',
                    outline: 'none',
                    border: 'none',
                    borderRadius: 4,
                    backgroundColor: theme.palette.tertiary?.veryLight,
                    '& .CardHeader': {
                        display: 'flex',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        '& > div:first-child': {
                            marginRight: theme.spacing(0.5),
                        },
                        '& > div:nth-child(2)': {
                            overflow: 'hidden',

                            '& h6': {
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                wordWrap: 'break-word',
                                fontWeight: 400,
                                lineHeight: '1.1',
                            },
                        },
                        '& > div:last-child': {
                            marginRight: theme.spacing(-1),
                        },
                    },
                    '& .CardOwner': {
                        '& .EditIcon': {
                            cursor: 'pointer',
                            paddingLeft: '5px',
                            display: 'inline-block',
                            verticalAlign: 'middle',
                            '& svg': {
                                fontSize: '1.2rem',
                            },
                            '&:hover': {
                                opacity: 0.7,
                            },
                        },
                    },
                    '& .CardContent': {
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        '& .CardLink': {
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            margin: theme.spacing(1),
                        },
                    },
                },
            },
        },
    },
    pagingDots: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2,
        '& svg': {
            fill: theme.palette.tertiary?.dark,
        },
        '&[aria-selected=true]': {
            '& svg': {
                width: '10px',
                height: '10px',
            },
        },
    },
});

export const FremhevdeKommentarer = () => {
    const dispatch = useDispatch();
    const classes = useKlpStyles({}, styles);
    const [chosenComment, setChosenComment] = useState<IKommentar | undefined>(
        undefined,
    );
    const { selectedContext } = useSelector(contextSelectors);
    const { saveHighlightedCommentsToRemoveToStorage } =
        kommentarerSelectorActions;
    const { highlightedCommentsToRemove } = useSelector(kommentarerSelectors);

    const [
        getKommentarer,
        {
            data: kommentarerData,
            isLoading: kommentarerIsLoading,
            isFetching: kommentarerIsFetching,
        },
    ] = useLazyGetKommentarerQuery();

    const highlightedComments = useMemo(
        () =>
            kommentarerData?.filter(
                (comment) =>
                    comment?.id &&
                    !highlightedCommentsToRemove.includes(comment.id),
            ) ?? [],
        [kommentarerData, highlightedCommentsToRemove],
    );

    const handleRemoveHighlight = (kommentarId?: string) => {
        if (!kommentarId) return;

        dispatch(
            saveHighlightedCommentsToRemoveToStorage([
                ...highlightedCommentsToRemove,
                kommentarId,
            ]),
        );
    };

    const handleModal = (kommentar: IKommentar) => {
        setChosenComment(kommentar);
    };

    useEffect(() => {
        if (!selectedContext?.id) return;
        getKommentarer({
            byggNummer: selectedContext?.id,
            kunfremhevde: true,
            maksTegn: SAMMENDRAG_MAKS_ANTALL_TEGN,
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedContext, highlightedCommentsToRemove]);

    return (
        <LocalLoader isLoading={kommentarerIsLoading || kommentarerIsFetching}>
            <Block paddingBottom="tiny">
                <Carousel
                    slidesToShow={3}
                    cellSpacing={16}
                    cellAlign="left"
                    defaultControlsConfig={{
                        nextButtonText: '›',
                        nextButtonClassName: classNames(classes.button, 'next'),
                        prevButtonText: '‹',
                        prevButtonClassName: classNames(classes.button, 'prev'),
                        pagingDotsClassName: classes.pagingDots,
                    }}
                    withoutControls={highlightedComments.length < 4}
                    className={classes.carousel}
                >
                    {highlightedComments.map((kommentar) => {
                        return (
                            <div className="CardWrapper" key={kommentar.id}>
                                <FremhevdeKommentarerCard
                                    kommentar={kommentar}
                                    handleModal={handleModal}
                                    handleRemoveHighlight={
                                        handleRemoveHighlight
                                    }
                                />
                            </div>
                        );
                    })}
                </Carousel>
            </Block>
            <KommentarDetaljer
                open={Boolean(chosenComment)}
                onClose={() => {
                    setChosenComment(undefined);
                }}
                comment={chosenComment}
            />
        </LocalLoader>
    );
};
